import { createRouter, createWebHistory } from "vue-router";
import { useAuthStatus } from "@/composables/api/auth";

function idProps(route: any) {
    return {
        ...route.params,
        id: Number(route.params.id),
    };
}

const routes = [
    {
        path: "/",
        name: "root",
    },
    {
        path: "/login",
        component: () => import("./layouts/AuthContent.vue"),
        meta: {
            title: "Login",
            linkRoute: { name: "forgot-password" },
            linkText: "Forgotten your password?",
        },
        children: [
            {
                path: "",
                name: "login",
                component: () => import("./views/auth/Login.vue"),
            },
        ],
    },
    {
        path: "/forgot-password",
        component: () => import("./layouts/AuthContent.vue"),
        meta: {
            title: "Forgotten Password",
            linkRoute: { name: "login" },
            linkText: "Back to Login",
        },
        children: [
            {
                path: "",
                name: "forgot-password",
                component: () => import("./views/auth/ForgotPassword.vue"),
            },
        ],
    },
    {
        path: "/register",
        component: () => import("./layouts/AuthContent.vue"),
        meta: {
            title: "Register",
            linkRoute: { name: "login" },
            linkText: "Already have an account? Login",
        },
        children: [
            {
                path: "",
                name: "register",
                component: () => import("./views/auth/Register.vue"),
            },
        ],
    },
    {
        path: "/reset-password",
        component: () => import("./layouts/AuthContent.vue"),
        meta: {
            title: "Reset Password",
            linkRoute: { name: "login" },
            linkText: "Back to Login",
        },
        children: [
            {
                path: "",
                name: "reset-password",
                component: () => import("./views/auth/ResetPassword.vue"),
            },
        ],
    },
    {
        path: "/app",
        name: "app",
        component: () => import("./views/App.vue"),
        redirect: { name: "home" },
        children: [
            {
                path: "home",
                name: "home",
                component: () => import("./views/app/Home.vue"),
                redirect: { name: "home.trusts" },
                children: [
                    {
                        path: "trusts",
                        name: "home.trusts",
                        component: () => import("./views/app/home/Trusts.vue"),
                    },
                    {
                        path: "schools",
                        name: "home.schools",
                        component: () => import("./views/app/home/Schools.vue"),
                    },
                ],
            },
            {
                path: "trust/:id",
                name: "trust",
                component: () => import("./views/app/Trust.vue"),
                props: idProps,
                redirect: { name: "trust.schools" },
                children: [
                    {
                        path: "schools",
                        name: "trust.schools",
                        props: idProps,
                        component: () =>
                            import("./views/app/trust/Schools.vue"),
                    },
                    {
                        path: "analysis",
                        name: "trust.analysis",
                        props: idProps,
                        component: () =>
                            import("./views/app/trust/Analysis.vue"),
                        children: [
                            {
                                path: ":analysisPageId",
                                name: "trust.analysis-page",
                                props: function (route: any) {
                                    return {
                                        ...route.params,
                                        id: Number(route.params.id),
                                        analysisPageId: Number(
                                            route.params.analysisPageId,
                                        ),
                                    };
                                },
                                component: () =>
                                    import(
                                        "./views/app/trust/AnalysisPage.vue"
                                    ),
                            },
                        ],
                    },

                    {
                        path: "metrics",
                        name: "trust.metrics",
                        props: idProps,
                        component: () =>
                            import("./views/app/trust/Metrics.vue"),
                    },
                    {
                        path: "settings",
                        name: "trust.settings",
                        props: idProps,
                        component: () =>
                            import("./views/app/trust/Settings.vue"),
                        redirect: { name: "trust.settings.details" },
                        children: [
                            {
                                path: "details",
                                name: "trust.settings.details",
                                props: idProps,
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/Details.vue"
                                    ),
                            },
                            {
                                path: "users",
                                name: "trust.settings.users",
                                props: idProps,
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/TrustUsers.vue"
                                    ),
                            },
                            {
                                path: "licences",
                                name: "trust.settings.licences",
                                props: idProps,
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/Licences.vue"
                                    ),
                            },
                            {
                                path: "schools",
                                name: "trust.settings.schools",
                                props: idProps,
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/Schools.vue"
                                    ),
                            },
                            {
                                path: "school/:schoolId/settings",
                                name: "trust.settings.school.settings",
                                props: function (route: any) {
                                    return {
                                        ...route.params,
                                        id: Number(route.params.id),
                                        schoolId: Number(route.params.schoolId),
                                    };
                                },
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/SchoolSettings.vue"
                                    ),
                                redirect: {
                                    name: "trust.settings.school.settings.details",
                                },
                                children: [
                                    {
                                        path: "details",
                                        name: "trust.settings.school.settings.details",
                                        props: function (route: any) {
                                            return {
                                                ...route.params,
                                                id: Number(
                                                    route.params.schoolId,
                                                ),
                                            };
                                        },
                                        component: () =>
                                            import(
                                                "./views/app/school/settings/Details.vue"
                                            ),
                                    },
                                    {
                                        path: "users",
                                        name: "trust.settings.school.settings.users",
                                        props: function (route: any) {
                                            return {
                                                ...route.params,
                                                id: Number(
                                                    route.params.schoolId,
                                                ),
                                            };
                                        },
                                        component: () =>
                                            import(
                                                "./views/app/school/settings/SchoolUsers.vue"
                                            ),
                                    },
                                ],
                            },
                            {
                                path: "wizard",
                                name: "trust.settings.wizard",
                                props: idProps,
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/Wizards.vue"
                                    ),
                            },
                            {
                                path: "wizard/:wizardId/details",
                                name: "trust.settings.wizard.details",
                                props: function (route: any) {
                                    return {
                                        ...route.params,
                                        id: Number(route.params.id),
                                        wizardId: Number(route.params.wizardId),
                                    };
                                },
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/WizardDetails.vue"
                                    ),
                            },
                            {
                                path: "entity-pair",
                                name: "trust.settings.entity-pair",
                                props: idProps,
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/EntityPairs.vue"
                                    ),
                            },
                            {
                                path: "entity-pair-fields/:entityPairId/:entityType",
                                name: "trust.settings.entity-pair-fields",
                                props: function (route: any) {
                                    return {
                                        ...route.params,
                                        id: Number(route.params.id),
                                        entityPairId: Number(
                                            route.params.entityPairId,
                                        ),
                                        entityType: route.params.entityType,
                                    };
                                },
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/EntityPairFields.vue"
                                    ),
                            },
                            {
                                path: "entity-pair-fields/:entityPairId/defaults",
                                name: "trust.settings.entity-pair-fields.defaults",
                                props: function (route: any) {
                                    return {
                                        ...route.params,
                                        id: Number(route.params.id),
                                        entityPairId: Number(
                                            route.params.entityPairId,
                                        ),
                                    };
                                },
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/DefaultEntityPairFields.vue"
                                    ),
                            },
                            {
                                path: "field/:fieldId/edit",
                                name: "trust.settings.field.edit",
                                props: function (route: any) {
                                    return {
                                        ...route.params,
                                        id: Number(route.params.id),
                                        fieldId: Number(route.params.fieldId),
                                    };
                                },
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/EditField.vue"
                                    ),
                            },
                            {
                                path: "entity-pair-fields/:entityPairId/:entityType/add",
                                name: "trust.settings.entity-pair-fields.add",
                                props: function (route: any) {
                                    return {
                                        ...route.params,
                                        id: Number(route.params.id),
                                        entityPairId: Number(
                                            route.params.entityPairId,
                                        ),
                                        entityType: route.params.entityType,
                                    };
                                },
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/AddField.vue"
                                    ),
                            },
                            {
                                path: "constants",
                                name: "trust.settings.constants",
                                props: idProps,
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/Constants.vue"
                                    ),
                            },
                            {
                                path: "constant/add",
                                name: "trust.settings.constant.add",
                                props: idProps,
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/AddConstant.vue"
                                    ),
                            },
                            {
                                path: "constant/:constantId/edit",
                                name: "trust.settings.constant.edit",
                                props: function (route: any) {
                                    return {
                                        ...route.params,
                                        id: Number(route.params.id),
                                        constantId: Number(
                                            route.params.constantId,
                                        ),
                                    };
                                },
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/EditConstant.vue"
                                    ),
                            },
                            {
                                path: "select",
                                name: "trust.settings.select",
                                props: idProps,
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/Selects.vue"
                                    ),
                            },
                            {
                                path: "select/add",
                                name: "trust.settings.select.add",
                                props: idProps,
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/AddSelect.vue"
                                    ),
                            },
                            {
                                path: "select/:selectId/edit",
                                name: "trust.settings.select.edit",
                                props: function (route: any) {
                                    return {
                                        ...route.params,
                                        id: Number(route.params.id),
                                        selectId: Number(route.params.selectId),
                                    };
                                },
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/EditSelect.vue"
                                    ),
                            },
                            {
                                path: "analysis-page",
                                name: "trust.settings.analysis-page",
                                props: idProps,
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/AnalysisPages.vue"
                                    ),
                            },
                            {
                                path: "analysis-page/add",
                                name: "trust.settings.analysis-page.add",
                                props: idProps,
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/AddAnalysisPage.vue"
                                    ),
                            },
                            {
                                path: "analysis-page/:analysisPageId/edit",
                                name: "trust.settings.analysis-page.edit",
                                props: function (route: any) {
                                    return {
                                        ...route.params,
                                        id: Number(route.params.id),
                                        analysisPageId: Number(
                                            route.params.analysisPageId,
                                        ),
                                    };
                                },
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/EditAnalysisPage.vue"
                                    ),
                            },
                            {
                                path: "map",
                                props: idProps,
                                name: "trust.settings.map",
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/Maps.vue"
                                    ),
                            },
                            {
                                path: "map/:mapId/edit",
                                name: "trust.settings.map.edit",
                                props: function (route: any) {
                                    return {
                                        ...route.params,
                                        id: Number(route.params.id),
                                        mapId: Number(route.params.mapId),
                                    };
                                },
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/EditMap.vue"
                                    ),
                            },
                            {
                                path: "map/add",
                                name: "trust.settings.map.add",
                                props: idProps,
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/AddMap.vue"
                                    ),
                            },
                            {
                                path: "metric",
                                name: "trust.settings.metric",
                                props: idProps,
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/Metrics.vue"
                                    ),
                            },
                            {
                                path: "benchmark",
                                name: "trust.settings.benchmark",
                                props: idProps,
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/Benchmarks.vue"
                                    ),
                            },
                            {
                                path: "benchmark/add",
                                name: "trust.settings.benchmark.add",
                                props: idProps,
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/AddBenchmark.vue"
                                    ),
                            },
                            {
                                path: "benchmark/:benchmarkId/edit",
                                name: "trust.settings.benchmark.edit",
                                props: function (route: any) {
                                    return {
                                        ...route.params,
                                        id: Number(route.params.id),
                                        benchmarkId: Number(
                                            route.params.benchmarkId,
                                        ),
                                    };
                                },
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/EditBenchmark.vue"
                                    ),
                            },
                            {
                                path: "metrics/add",
                                name: "trust.settings.metric.add",
                                props: idProps,
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/AddMetric.vue"
                                    ),
                            },
                            {
                                path: "metrics/:metricId/edit",
                                name: "trust.settings.metric.edit",
                                props: function (route: any) {
                                    return {
                                        ...route.params,
                                        id: Number(route.params.id),
                                        metricId: Number(route.params.metricId),
                                    };
                                },
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/EditMetric.vue"
                                    ),
                            },
                            {
                                path: "tag",
                                name: "trust.settings.tag",
                                props: idProps,
                                component: () =>
                                    import(
                                        "./views/app/trust/settings/Tags.vue"
                                    ),
                            },
                        ],
                    },
                ],
            },
            {
                path: "school/:id",
                name: "school",
                component: () => import("./views/app/School.vue"),
                props: idProps,
                redirect: { name: "school.curriculum-staffing-models" },
                children: [
                    {
                        path: "settings",
                        name: "school.settings",
                        props: idProps,
                        component: () =>
                            import("./views/app/school/Settings.vue"),
                        redirect: { name: "school.settings.details" },
                        children: [
                            {
                                path: "details",
                                name: "school.settings.details",
                                props: idProps,
                                component: () =>
                                    import(
                                        "./views/app/school/settings/Details.vue"
                                    ),
                            },
                            {
                                path: "users",
                                name: "school.settings.users",
                                props: idProps,
                                component: () =>
                                    import(
                                        "./views/app/school/settings/SchoolUsers.vue"
                                    ),
                            },
                        ],
                    },
                    {
                        path: "curriculum-staffing-models",
                        name: "school.curriculum-staffing-models",
                        props: idProps,
                        component: () =>
                            import("./views/app/school/Models.vue"),
                    },
                    {
                        path: "analysis",
                        name: "school.analysis",
                        props: idProps,
                        component: () =>
                            import("./views/app/school/Analysis.vue"),
                        children: [
                            {
                                path: ":analysisPageId",
                                name: "school.analysis-page",
                                props: function (route: any) {
                                    return {
                                        ...route.params,
                                        id: Number(route.params.id),
                                        analysisPageId: Number(
                                            route.params.analysisPageId,
                                        ),
                                    };
                                },
                                component: () =>
                                    import(
                                        "./views/app/school/AnalysisPage.vue"
                                    ),
                            },
                        ],
                    },
                    {
                        path: "metrics",
                        name: "school.metrics",
                        props: idProps,
                        component: () =>
                            import("./views/app/school/Metrics.vue"),
                    },
                ],
            },
            {
                path: "curriculum-staffing-model/:id",
                name: "curriculum-staffing-model",
                props: idProps,
                component: () =>
                    import("./views/app/CurriculumStaffingModel.vue"),
                redirect: { name: "curriculum-staffing-model.entity-pairs" },
                children: [
                    {
                        path: "settings",
                        name: "curriculum-staffing-model.settings",
                        props: idProps,
                        component: () =>
                            import(
                                "./views/app/curriculumStaffingModel/Settings.vue"
                            ),
                        redirect: {
                            name: "curriculum-staffing-model.settings.details",
                        },
                        children: [
                            {
                                path: "details",
                                name: "curriculum-staffing-model.settings.details",
                                props: idProps,
                                component: () =>
                                    import(
                                        "./views/app/curriculumStaffingModel/settings/Details.vue"
                                    ),
                            },
                            {
                                path: "tags",
                                name: "curriculum-staffing-model.settings.tags",
                                props: idProps,
                                component: () =>
                                    import(
                                        "./views/app/curriculumStaffingModel/settings/Tags.vue"
                                    ),
                            },
                            {
                                path: "constants",
                                name: "curriculum-staffing-model.settings.constants",
                                props: idProps,
                                component: () =>
                                    import(
                                        "./views/app/curriculumStaffingModel/settings/Constants.vue"
                                    ),
                            },
                        ],
                    },
                    {
                        path: "entity-pairs",
                        name: "curriculum-staffing-model.entity-pairs",
                        props: idProps,
                        component: () =>
                            import(
                                "./views/app/curriculumStaffingModel/Tables.vue"
                            ),
                        children: [
                            {
                                path: ":entityPairId",
                                name: "curriculum-staffing-model.entity-pair",
                                props: function (route: any) {
                                    return {
                                        ...route.params,
                                        id: Number(route.params.id),
                                        entityPairId: Number(
                                            route.params.entityPairId,
                                        ),
                                    };
                                },
                                component: () =>
                                    import(
                                        "./views/app/curriculumStaffingModel/EntityPair.vue"
                                    ),
                            },
                        ],
                    },
                    {
                        path: "constants",
                        name: "curriculum-staffing-model.constants",
                        props: idProps,
                        component: () =>
                            import(
                                "./views/app/curriculumStaffingModel/Constants.vue"
                            ),
                    },
                    {
                        path: "analysis",
                        name: "curriculum-staffing-model.analysis",
                        props: idProps,
                        component: () =>
                            import(
                                "./views/app/curriculumStaffingModel/Analysis.vue"
                            ),
                        children: [
                            {
                                path: ":analysisPageId",
                                name: "curriculum-staffing-model.analysis-page",
                                props: function (route: any) {
                                    return {
                                        ...route.params,
                                        id: Number(route.params.id),
                                        analysisPageId: Number(
                                            route.params.analysisPageId,
                                        ),
                                    };
                                },
                                component: () =>
                                    import(
                                        "./views/app/curriculumStaffingModel/AnalysisPage.vue"
                                    ),
                            },
                        ],
                    },
                    {
                        path: "metrics",
                        name: "curriculum-staffing-model.metrics",
                        props: idProps,
                        component: () =>
                            import(
                                "./views/app/curriculumStaffingModel/Metrics.vue"
                            ),
                    },
                    {
                        path: "activity",
                        name: "curriculum-staffing-model.activity",
                        props: idProps,
                        component: () =>
                            import(
                                "./views/app/curriculumStaffingModel/Activity.vue"
                            ),
                    },
                ],
            },
            {
                path: "profile",
                name: "profile",
                component: () => import("./views/app/Profile.vue"),
                redirect: { name: "profile.details" },
                children: [
                    {
                        path: "details",
                        name: "profile.details",
                        component: () =>
                            import("./views/app/profile/Details.vue"),
                    },
                    {
                        path: "password",
                        name: "profile.password",
                        component: () =>
                            import("./views/app/profile/Password.vue"),
                    },
                    {
                        path: "preferences",
                        name: "profile.preferences",
                        component: () =>
                            import("./views/app/profile/Preferences.vue"),
                    },
                ],
            },
            {
                path: "admin",
                name: "admin",
                component: () => import("./views/app/Admin.vue"),
                redirect: { name: "admin.users" },
                children: [
                    {
                        path: "users",
                        name: "admin.users",
                        component: () => import("./views/app/admin/Users.vue"),
                    },
                    {
                        path: "trusts",
                        name: "admin.trusts",
                        component: () => import("./views/app/admin/Trusts.vue"),
                    },
                    {
                        path: "licences",
                        name: "admin.licences",
                        component: () =>
                            import("./views/app/admin/Licences.vue"),
                    },
                    {
                        path: "wizards",
                        name: "admin.wizards",
                        component: () =>
                            import("./views/app/admin/WizardManager.vue"),
                    },
                    {
                        path: "server-control",
                        name: "admin.server-control",
                        component: () =>
                            import("./views/app/admin/ServerControl.vue"),
                    },
                    {
                        path: "activity-logs",
                        name: "admin.activity-logs",
                        component: () =>
                            import("./views/app/admin/ActivityLogs.vue"),
                    },
                    {
                        path: "email-logs",
                        name: "admin.email-logs",
                        component: () =>
                            import("./views/app/admin/EmailLogs.vue"),
                    },
                ],
            },
        ],
    },
    {
        path: "/error/:code",
        name: "error",
        component: () => import("./views/Error.vue"),
        props: function (route: any) {
            return {
                code: Number(route.params.code),
            };
        },
    },
    // Catch-all route for 404 Not Found pages
    {
        path: "/:pathMatch(.*)*",
        component: () => import("./views/Error.vue"),
        props: { code: 404 },
    },
];

const router = createRouter({
    history: createWebHistory("/"),
    // @ts-ignore
    routes,
});

router.beforeEach(async (to, _from, next) => {
    if (to.path === "/") {
        next({ name: "home" });
        return;
    }

    const isAuthenticated = await useAuthStatus();

    if (to.path.startsWith("/app") && !isAuthenticated) {
        next({ name: "login" });
    } else {
        next();
    }
});
export default router;
